import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { DefaultChildRedirect } from "../app/navigation/routing/default-child-redirect";
import { MerchantRouteNavigate } from "../redo-suite/merchant-routes";
import {
  CarrierClaimsListBreadcrumbLayout,
  ClaimsListBreadcrumbLayout,
} from "./claims-breadcrumb-label";
import { RecordHomeExperienceGate } from "./record-home/experience/record-home-experience-gate";
import {
  ReturnsListBreadcrumbLayout,
  TranslatedBreadcrumbLabel,
  TranslatedBreadcrumbLayout,
} from "./returns-breadcrumb-label";
import { RecordViewTabsProvider } from "./view-tabs/record-view-tabs-context";
import { RecordViewTabsGate } from "./view-tabs/record-view-tabs-gate";

const returnBreadcrumb = breadcrumbSlot();
const returnFolderBreadcrumb = breadcrumbSlot();
const returnViewBreadcrumb = breadcrumbSlot();
const returnsListBreadcrumb = breadcrumbSlot();

const claimsListBreadcrumb = breadcrumbSlot();
const claimFolderBreadcrumb = breadcrumbSlot();
const claimViewBreadcrumb = breadcrumbSlot();
const carrierClaimsListBreadcrumb = breadcrumbSlot();

const warrantyFolderBreadcrumb = breadcrumbSlot();
const warrantyViewBreadcrumb = breadcrumbSlot();

const recycleFolderBreadcrumb = breadcrumbSlot();
const recycleViewBreadcrumb = breadcrumbSlot();

const carrierClaimDetailBreadcrumb = breadcrumbSlot();

const instantExchangeRecoveryBreadcrumb = breadcrumbSlot();
const giftReceiptRequestsBreadcrumb = breadcrumbSlot();
const inmarBlindReturnsBreadcrumb = breadcrumbSlot();
const fraudBreadcrumb = breadcrumbSlot();
const warrantiesBreadcrumb = breadcrumbSlot();
const recycleBreadcrumb = breadcrumbSlot();
const withdrawalsBreadcrumb = breadcrumbSlot();
const allWithdrawalsBreadcrumb = breadcrumbSlot();
const needsActionWithdrawalsBreadcrumb = breadcrumbSlot();

// The return detail page renders its own header inside the left column, so it
// opts out of the shared page header. `Page` reads this via `useMatches`.
const returnDetailHandle = { breadcrumb: returnBreadcrumb, hideHeader: true };

export const returnRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <RecordViewTabsProvider>
        <ReturnsListBreadcrumbLayout breadcrumb={returnsListBreadcrumb} />
        <RecordViewTabsGate type="returns" />
      </RecordViewTabsProvider>
    ),
    handle: { breadcrumb: returnsListBreadcrumb },
    children: [
      {
        index: true,
        element: (
          <RecordHomeExperienceGate
            legacy={<DefaultChildRedirect child="all-returns" />}
            type="returns"
          />
        ),
      },
      {
        path: "all-returns",
        handle: { breadcrumb: returnViewBreadcrumb },
        children: [
          {
            index: true,
            async lazy() {
              const { ReturnWrapper } = await import("./returns");
              return {
                element: (
                  <ReturnWrapper
                    folderBreadcrumb={returnFolderBreadcrumb}
                    viewBreadcrumb={returnViewBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: ":returnId",
            handle: returnDetailHandle,
            children: [
              {
                index: true,
                async lazy() {
                  const { WrappedReturnPage } = await import("./return");
                  return {
                    element: (
                      <WrappedReturnPage
                        breadcrumb={returnBreadcrumb}
                        returnType="return"
                        viewBreadcrumb={returnViewBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          breadcrumb: returnFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: returnViewBreadcrumb },
            async lazy() {
              const { ReturnWrapper } = await import("./returns");
              return {
                element: (
                  <ReturnWrapper
                    folderBreadcrumb={returnFolderBreadcrumb}
                    viewBreadcrumb={returnViewBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: ":returnId",
            handle: returnDetailHandle,
            children: [
              {
                index: true,
                async lazy() {
                  const { WrappedReturnPage } = await import("./return");
                  return {
                    element: (
                      <WrappedReturnPage
                        breadcrumb={returnBreadcrumb}
                        returnType="return"
                        viewBreadcrumb={returnViewBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-return/:orderId",
        children: [
          {
            index: true,
            async lazy() {
              const { CreateReturnPage: CreateReturn } =
                await import("./create-return");
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.RETURN}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-third-party-return",
        async lazy() {
          const { CreateThirdPartyReturnPage } =
            await import("./retail-portal");
          return {
            element: (
              <CreateThirdPartyReturnPage
                breadcrumb={returnBreadcrumb}
                type={ReturnTypeEnum.RETURN}
              />
            ),
          };
        },
      },
      {
        path: "instant-exchange",
        children: [
          {
            index: true,
            handle: { breadcrumb: instantExchangeRecoveryBreadcrumb },
            async lazy() {
              const { InstantExchangeRecovery } =
                await import("./instant-exchange-recovery");
              return {
                element: (
                  <>
                    <TranslatedBreadcrumbLabel
                      breadcrumb={instantExchangeRecoveryBreadcrumb}
                      getLabel={(t) => t("Instant exchange recovery")}
                    />
                    <InstantExchangeRecovery />
                  </>
                ),
              };
            },
          },
        ],
      },
      {
        path: "gift-receipt-requests",
        handle: { breadcrumb: giftReceiptRequestsBreadcrumb },
        async lazy() {
          const { GiftReceiptRequests } =
            // redont-allow-dynamic-import: route-level code splitting for carved general/app package
            await import("@redotech/redo-merchant-general-app/gift-receipt-requests/gift-receipt-requests");
          return {
            element: (
              <>
                <TranslatedBreadcrumbLabel
                  breadcrumb={giftReceiptRequestsBreadcrumb}
                  getLabel={(t) => t("Gift Receipt Requests")}
                />
                <GiftReceiptRequests />
              </>
            ),
          };
        },
      },
      {
        path: "inmar-blind-returns",
        handle: { breadcrumb: inmarBlindReturnsBreadcrumb },
        async lazy() {
          const { InmarBlindReturns } =
            // redont-allow-dynamic-import: route-level code splitting for carved general/app package
            await import("@redotech/redo-merchant-general-app/inmar-blind-returns/inmar-blind-returns");
          return {
            element: (
              <>
                <TranslatedBreadcrumbLabel
                  breadcrumb={inmarBlindReturnsBreadcrumb}
                  getLabel={(t) => t("Inmar Blind Returns")}
                />
                <InmarBlindReturns />
              </>
            ),
          };
        },
      },
    ],
  },
];

export const fraudRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <TranslatedBreadcrumbLayout
        breadcrumb={fraudBreadcrumb}
        getLabel={(t) => t("Fraud")}
      />
    ),
    handle: { breadcrumb: fraudBreadcrumb },
    children: [
      {
        index: true,
        async lazy() {
          const { FraudDashboardPage } =
            await import("./fraud/fraud-dashboard");
          return { element: <FraudDashboardPage /> };
        },
      },
    ],
  },
];

export const carrierClaimsRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <CarrierClaimsListBreadcrumbLayout
        breadcrumb={carrierClaimsListBreadcrumb}
      />
    ),
    handle: {
      breadcrumb: carrierClaimsListBreadcrumb,
      permission: [ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM],
    },
    children: [
      {
        index: true,
        async lazy() {
          const { MerchantShippingClaimsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/shipping-claims/merchant-shipping-claims-page");
          return { element: <MerchantShippingClaimsPage /> };
        },
      },
      {
        path: ":claim_id",
        handle: { breadcrumb: carrierClaimDetailBreadcrumb },
        async lazy() {
          const { MerchantShippingClaimDetailPage } =
            await import("@redotech/redo-merchant-fulfillment-app/shipping-claims/merchant-shipping-claim-detail-page");
          return {
            element: (
              <MerchantShippingClaimDetailPage
                breadcrumb={carrierClaimDetailBreadcrumb}
              />
            ),
          };
        },
      },
    ],
  },
];

const createClaimIdRoute = (): PermissionedRouteObject => ({
  handle: returnDetailHandle,
  path: ":returnId",
  children: [
    {
      index: true,
      async lazy() {
        const { WrappedReturnPage } = await import("./return");
        return {
          element: (
            <WrappedReturnPage
              breadcrumb={returnBreadcrumb}
              returnType="claim"
              viewBreadcrumb={claimViewBreadcrumb}
            />
          ),
        };
      },
    },
  ],
});

export const claimRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <RecordViewTabsProvider>
        <ClaimsListBreadcrumbLayout breadcrumb={claimsListBreadcrumb} />
        <RecordViewTabsGate type="claims" />
      </RecordViewTabsProvider>
    ),
    handle: { breadcrumb: claimsListBreadcrumb },
    children: [
      {
        index: true,
        element: (
          <RecordHomeExperienceGate
            legacy={
              <MerchantRouteNavigate
                routeTo={(routes) => routes.claimsDefaultRoute}
              />
            }
            type="claims"
          />
        ),
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-claim/:orderId",
        children: [
          {
            index: true,
            async lazy() {
              const { CreateReturnPage: CreateReturn } =
                await import("./create-return");
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.CLAIM}
                  />
                ),
              };
            },
          },
        ],
      },
      // Needs a separate route because the navigation item is a child of the index route
      {
        path: "all",
        handle: {
          breadcrumb: claimViewBreadcrumb,
          permission: [
            ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ClaimWrapper } = await import("./claims");
              return {
                element: (
                  <ClaimWrapper
                    folderBreadcrumb={claimFolderBreadcrumb}
                    viewBreadcrumb={claimViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createClaimIdRoute(),
        ],
      },
      { path: "carrier-claims", children: carrierClaimsRoutes },
      {
        handle: {
          breadcrumb: claimFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: claimViewBreadcrumb },
            async lazy() {
              const { ClaimWrapper } = await import("./claims");
              return {
                element: (
                  <ClaimWrapper
                    folderBreadcrumb={claimFolderBreadcrumb}
                    viewBreadcrumb={claimViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createClaimIdRoute(),
        ],
      },
    ],
  },
];

const createWarrantyIdRoute = (): PermissionedRouteObject => ({
  handle: returnDetailHandle,
  path: ":returnId",
  children: [
    {
      index: true,
      async lazy() {
        const { WrappedReturnPage } = await import("./return");
        return {
          element: (
            <WrappedReturnPage
              breadcrumb={returnBreadcrumb}
              returnType="warranty"
              viewBreadcrumb={warrantyViewBreadcrumb}
            />
          ),
        };
      },
    },
  ],
});

export const warrantyRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <RecordViewTabsProvider>
        <TranslatedBreadcrumbLayout
          breadcrumb={warrantiesBreadcrumb}
          getLabel={(t) => t("Warranties")}
        />
        <RecordViewTabsGate type="warranties" />
      </RecordViewTabsProvider>
    ),
    handle: { breadcrumb: warrantiesBreadcrumb },
    children: [
      {
        index: true,
        async lazy() {
          const { WarrantyWrapper } = await import("./warranties");
          return {
            element: (
              <RecordHomeExperienceGate
                legacy={
                  <WarrantyWrapper
                    folderBreadcrumb={warrantyFolderBreadcrumb}
                    viewBreadcrumb={warrantyViewBreadcrumb}
                  />
                }
                type="warranties"
              />
            ),
          };
        },
      },
      {
        path: "about",
        async lazy() {
          const { AboutWarranties } = await import("./about-warranties");
          return { element: <AboutWarranties /> };
        },
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-warranty-claim",
        children: [
          {
            path: "registration/:registrationId",
            async lazy() {
              const { CreateReturnPage: CreateReturn } =
                await import("./create-return");
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.WARRANTY}
                  />
                ),
              };
            },
          },
          {
            path: ":orderId",
            async lazy() {
              const { CreateReturnPage: CreateReturn } =
                await import("./create-return");
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.WARRANTY}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-third-party-warranty-claim",
        async lazy() {
          const { CreateThirdPartyReturnPage } =
            await import("./retail-portal");
          return {
            element: (
              <CreateThirdPartyReturnPage
                breadcrumb={returnBreadcrumb}
                type={ReturnTypeEnum.WARRANTY}
              />
            ),
          };
        },
      },
      {
        path: "all-warranties",
        handle: {
          breadcrumb: warrantyViewBreadcrumb,
          permission: [ReturnsAndClaimsPermissions.MANAGE_WARRANTY],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WarrantyWrapper } = await import("./warranties");
              return {
                element: (
                  <WarrantyWrapper
                    folderBreadcrumb={warrantyFolderBreadcrumb}
                    viewBreadcrumb={warrantyViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createWarrantyIdRoute(),
        ],
      },
      {
        handle: {
          breadcrumb: warrantyFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: warrantyViewBreadcrumb },
            async lazy() {
              const { WarrantyWrapper } = await import("./warranties");
              return {
                element: (
                  <WarrantyWrapper
                    folderBreadcrumb={warrantyFolderBreadcrumb}
                    viewBreadcrumb={warrantyViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createWarrantyIdRoute(),
        ],
      },
    ],
  },
];

const createRecycleIdRoute = (): PermissionedRouteObject => ({
  handle: returnDetailHandle,
  path: ":returnId",
  children: [
    {
      index: true,
      async lazy() {
        const { WrappedReturnPage } = await import("./return");
        return {
          element: (
            <WrappedReturnPage
              breadcrumb={returnBreadcrumb}
              returnType="recycle"
              viewBreadcrumb={recycleViewBreadcrumb}
            />
          ),
        };
      },
    },
  ],
});

const withdrawalRecordBreadcrumb = breadcrumbSlot();

// The withdrawal detail component may delegate to the return detail page when
// the withdrawal is backed by a return. Keep the shared header hidden so that
// branch does not render a duplicate header; the order-only branch renders its
// own header.
const createWithdrawalRecordIdRoute = (): PermissionedRouteObject => ({
  handle: { breadcrumb: withdrawalRecordBreadcrumb, hideHeader: true },
  path: ":withdrawalId",
  children: [
    {
      index: true,
      async lazy() {
        const { WithdrawalDetailsPage } =
          await import("./withdrawals/withdrawal-details-page");
        return {
          element: (
            <WithdrawalDetailsPage breadcrumb={withdrawalRecordBreadcrumb} />
          ),
        };
      },
    },
  ],
});

export const recycleRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <RecordViewTabsProvider>
        <TranslatedBreadcrumbLayout
          breadcrumb={recycleBreadcrumb}
          getLabel={(t) => t("Recycle")}
        />
        <RecordViewTabsGate type="recycle" />
      </RecordViewTabsProvider>
    ),
    handle: { breadcrumb: recycleBreadcrumb },
    children: [
      {
        index: true,
        async lazy() {
          const { RecycleWrapper } = await import("./recycle");
          return {
            element: (
              <RecycleWrapper
                folderBreadcrumb={recycleFolderBreadcrumb}
                viewBreadcrumb={recycleViewBreadcrumb}
              />
            ),
          };
        },
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-recycle-request/:orderId",
        children: [
          {
            index: true,
            async lazy() {
              const { CreateReturnPage: CreateReturn } =
                await import("./create-return");
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.RECYCLE}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "all-recycle",
        handle: {
          breadcrumb: recycleViewBreadcrumb,
          permission: [ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { RecycleWrapper } = await import("./recycle");
              return {
                element: (
                  <RecycleWrapper
                    folderBreadcrumb={recycleFolderBreadcrumb}
                    viewBreadcrumb={recycleViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createRecycleIdRoute(),
        ],
      },
      {
        handle: {
          breadcrumb: recycleFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: recycleViewBreadcrumb },
            async lazy() {
              const { RecycleWrapper } = await import("./recycle");
              return {
                element: (
                  <RecycleWrapper
                    folderBreadcrumb={recycleFolderBreadcrumb}
                    viewBreadcrumb={recycleViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createRecycleIdRoute(),
        ],
      },
    ],
  },
];

export const withdrawalRoutes: PermissionedRouteObject[] = [
  {
    element: (
      <>
        <TranslatedBreadcrumbLayout
          breadcrumb={withdrawalsBreadcrumb}
          getLabel={(t) => t("Withdrawals")}
        />
        <RecordViewTabsGate type="withdrawals" />
      </>
    ),
    handle: { breadcrumb: withdrawalsBreadcrumb },
    children: [
      {
        index: true,
        element: (
          <RecordHomeExperienceGate
            legacy={<DefaultChildRedirect child="all-withdrawals" />}
            type="withdrawals"
          />
        ),
      },
      {
        path: "all-withdrawals",
        element: (
          <TranslatedBreadcrumbLayout
            breadcrumb={allWithdrawalsBreadcrumb}
            getLabel={(t) => t("All withdrawals")}
          />
        ),
        handle: {
          breadcrumb: allWithdrawalsBreadcrumb,
          permission: [ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WithdrawalsView } =
                await import("./withdrawals/withdrawals-view");
              const { WithdrawalListView } =
                await import("@redotech/merchant-sdk/rpc/schema/withdrawals/list-withdrawals");
              return {
                element: <WithdrawalsView view={WithdrawalListView.ALL} />,
              };
            },
          },
          createWithdrawalRecordIdRoute(),
        ],
      },
      {
        path: "needs-action",
        element: (
          <TranslatedBreadcrumbLayout
            breadcrumb={needsActionWithdrawalsBreadcrumb}
            getLabel={(t) => t("Needs action")}
          />
        ),
        handle: {
          breadcrumb: needsActionWithdrawalsBreadcrumb,
          permission: ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WithdrawalsView } =
                await import("./withdrawals/withdrawals-view");
              const { WithdrawalListView } =
                await import("@redotech/merchant-sdk/rpc/schema/withdrawals/list-withdrawals");
              return {
                element: (
                  <WithdrawalsView view={WithdrawalListView.NEEDS_ACTION} />
                ),
              };
            },
          },
          createWithdrawalRecordIdRoute(),
        ],
      },
    ],
  },
];
